/**
 * PracticeScaffold — shared UI shell + panel bits used by every custom
 * Practice scene (V2, V3, V4, ...). Removes the ~150 lines of duplicated
 * scaffolding across FlagshipPracticeV*.jsx files.
 */
import React from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, ArrowRight, RotateCcw } from "lucide-react";

export const Panel = ({ title, stepIdx, total }) => (
  <div className="rounded-2xl bg-slate-900/80 border border-cyan-500/30 p-4 sm:p-5 shadow-xl">
    <div className="flex items-center gap-2 text-cyan-400 text-xs font-bold uppercase tracking-widest mb-2">
      <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-300 text-xs font-black">{stepIdx + 1}</div>
      Step {stepIdx + 1} of {total} · Practice
    </div>
    <p className="text-slate-100 leading-relaxed text-sm sm:text-base" data-testid="practice-instruction">{title}</p>
  </div>
);

export const Feedback = ({ msg }) => msg ? (
  <div
    className={`rounded-2xl p-4 border ${msg.type === "ok" ? "bg-emerald-900/40 border-emerald-500/50 text-emerald-200" : "bg-amber-900/40 border-amber-500/50 text-amber-200"}`}
    data-testid="practice-feedback"
  >
    <div className="flex gap-2">
      {msg.type === "ok" ? <CheckCircle2 className="h-5 w-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />}
      <div className="text-sm leading-relaxed">{msg.msg}</div>
    </div>
  </div>
) : null;

export const NextBar = ({ enabled, isLast, onNext, onReset }) => (
  <div className="flex gap-2">
    <Button size="sm" variant="outline" className="border-slate-700 text-slate-300 flex-1" onClick={onReset} data-testid="practice-reset">
      <RotateCcw className="mr-2 h-4 w-4" /> Reset
    </Button>
    <Button size="sm" className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold rounded-full" onClick={onNext} disabled={!enabled} data-testid="practice-next">
      {isLast ? "Go to Observe" : "Next step"} <ArrowRight className="ml-2 h-4 w-4" />
    </Button>
  </div>
);

export const Shell = ({ scene, panel }) => (
  <div className="h-full w-full flex flex-col lg:flex-row gap-4 min-h-0">
    <div className="w-full lg:flex-1 min-h-[300px] sm:min-h-[400px] lg:min-h-[500px] rounded-2xl bg-gradient-to-b from-slate-900 to-slate-950 border border-slate-800 overflow-hidden relative">
      {scene}
    </div>
    <div className="w-full lg:w-96 lg:shrink-0 flex flex-col gap-3 min-h-0">
      {panel}
    </div>
  </div>
);
